import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import SEOHead from './components/SEOHead';
import { CATEGORIES, CATEGORY_VALUE_MAP, getCategoryName } from './config/categories';
import Hero from './components/Hero'; // Eagerly loaded since it contains the LCP element
import './App.css';
import './responsive-utils.css';

// Lazy load components for better performance
const BlogList = React.lazy(() => import('./components/BlogList'));
const BlogDetail = React.lazy(() => import('./components/BlogDetail'));
const About = React.lazy(() => import('./components/About'));
const Contact = React.lazy(() => import('./components/Contact'));
const NotFound = React.lazy(() => import('./components/NotFound'));

function HomePage({
  blogs,
  filteredBlogs,
  loading,
  error,
  activeCategory,
  handleCategoryChange,
  categories,
  loadMoreBlogs,
  hasMore,
  loadingMore
}) {
  const isCategoryView = activeCategory !== 'all';

  // Generate SEO data for homepage with proper canonical URLs
  const getHomePageSEO = () => {
    if (isCategoryView) {
      const categoryName = getCategoryName(activeCategory);
      const formattedCategory = categoryName.replace(/\s+/g, ' ').trim();
      const catObj = CATEGORIES.find(c => c.id === activeCategory);
      return {
        title: `${formattedCategory} | DailyTechTrend`,
        description: catObj?.description
          ? `${catObj.description}. Get daily tech insights, guides, and trends at DailyTechTrend.`
          : `Explore the latest ${formattedCategory.toLowerCase()} articles, guides, and insights at DailyTechTrend.`,
        url: `https://dailytechtrend.com/?category=${activeCategory}`,
        canonicalUrl: `https://dailytechtrend.com/?category=${activeCategory}`,
        type: 'website'
      };
    }

    return {
      title: 'DailyTechTrend: Tech Trends, Venture, Apple & AI Tools News',
      description: 'Your daily source for the latest technology trends, venture news, Apple updates, AI tools reviews, and earning tips. Stay ahead with DailyTechTrend.',
      url: 'https://dailytechtrend.com/',
      canonicalUrl: 'https://dailytechtrend.com/',
      type: 'website'
    };
  };

  if (loading) return (
    <div className="App">
      <SEOHead
        title="Loading... | DailyTechTrend"
        description="Your daily source for the latest technology trends, venture news, Apple updates, AI tools reviews, and earning tips. Stay ahead with DailyTechTrend for daily tech insights."
        url="https://dailytechtrend.com/"
        canonicalUrl="https://dailytechtrend.com/"
        type="website"
      />
      <Navbar
        categories={categories}
        onCategoryChange={handleCategoryChange}
        activeCategory={activeCategory}
      />
      <div className="loading">
        <div className="loading-spinner"></div>
        <p>Loading daily tech articles...</p>
      </div>
      <Footer />
    </div>
  );

  if (error) return (
    <div className="App">
      <SEOHead
        title="Error Loading Content | DailyTechTrend"
        description="We encountered an issue loading the content. Please try again."
        url="https://dailytechtrend.com/"
        canonicalUrl="https://dailytechtrend.com/"
        type="website"
      />
      <Navbar
        categories={categories}
        onCategoryChange={handleCategoryChange}
        activeCategory={activeCategory}
      />
      <div className="error">
        <h2>Oops! Something went wrong</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()} className="retry-button">
          Try Again
        </button>
      </div>
      <Footer />
    </div>
  );

  return (
    <div className="App">
      <SEOHead {...getHomePageSEO()} />
      <Navbar
        categories={categories}
        onCategoryChange={handleCategoryChange}
        activeCategory={activeCategory}
      />
      <main>
        {/* Visible H1 for SEO keyword optimization */}
        <h1 className="site-main-heading">
          DailyTechTrend — Latest Tech Trends, Venture News, Apple Updates &amp; AI Tools
        </h1>

        {/* Highlights Section with Featured Blogs */}
        {filteredBlogs.length > 0 && (
          <Hero featuredBlogs={filteredBlogs} />
        )}

        {/* Topic Sub-Navigation (between Highlights and Latest) */}
        {!isCategoryView && categories.length > 1 && (
          <section className="nyt-topics-bar">
            <div className="container">
              <div className="nyt-topics-list">
                {categories.slice(1, 7).map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id)}
                    className="nyt-topic-link"
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Latest Articles Stream */}
        <Suspense fallback={<div className="loading" style={{ minHeight: '400px' }}>Loading blog posts...</div>}>
          <BlogList
            blogs={filteredBlogs}
            isCategoryView={activeCategory !== 'all'}
            showHero={true}
            onLoadMore={loadMoreBlogs}
            hasMore={hasMore}
            loading={loadingMore}
          />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

/**
 * Filter blogs by navbar category using the shared CATEGORY_VALUE_MAP.
 * Old blog GCP category values (ai, generative-ai, llm, etc.) are mapped
 * to the appropriate new category via CATEGORY_VALUE_MAP.
 */
const filterBlogsByCategory = (blogs, category) => {
  if (category === 'all') return blogs;

  const normalizedFilter = category.toLowerCase();

  return blogs.filter(blog => {
    // 1. Check if SEO.categories is a list (array)
    const rawCategories = blog.SEO?.categories || blog.categories;
    if (Array.isArray(rawCategories)) {
      return rawCategories.some(cat => {
        const normalizedCat = cat.toString().toLowerCase().trim();
        const mappedCat = CATEGORY_VALUE_MAP[normalizedCat] || normalizedCat;
        return mappedCat === normalizedFilter;
      });
    }

    // 2. Fallback to single category string
    const rawCategory = (
      blog.SEO?.category ||
      blog.metadata?.category ||
      blog.category ||
      ''
    ).toString().toLowerCase().trim();

    // Handle comma-separated category string
    if (rawCategory.includes(',')) {
      return rawCategory.split(',').some(cat => {
        const normalizedCat = cat.trim().toLowerCase();
        const mappedCat = CATEGORY_VALUE_MAP[normalizedCat] || normalizedCat;
        return mappedCat === normalizedFilter;
      });
    }

    const mappedCategory = CATEGORY_VALUE_MAP[rawCategory] || rawCategory;
    return mappedCategory === normalizedFilter;
  });
};

function AppWrapper() {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  // Use the shared CATEGORIES config
  const categories = CATEGORIES;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Sync category state with URL path or query params
  useEffect(() => {
    const path = location.pathname;
    const match = path.match(/^\/category\/([^/]+)\/?$/);
    if (match) {
      const cat = match[1];
      if (activeCategory !== cat) {
        setActiveCategory(cat);
      }
    } else if (path === '/') {
      const catParam = searchParams.get('category');
      if (catParam) {
        if (activeCategory !== catParam) {
          setActiveCategory(catParam);
        }
      } else {
        if (activeCategory !== 'all') {
          setActiveCategory('all');
        }
      }
    }
  }, [location.pathname, searchParams, activeCategory]);

  // Infinite scroll state
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadedBatches, setLoadedBatches] = useState(0);

  // Apply filtering whenever blogs or activeCategory changes
  useEffect(() => {
    const filtered = filterBlogsByCategory(blogs, activeCategory);
    setFilteredBlogs(filtered);

    // If we have very few results (e.g. < 5) and still have more batches, 
    // we could optionally auto-load more here, but per user request 
    // we sticking to "load next batch when user scroll".
    console.log(`🔍 Filter applied: ${activeCategory}. Matched ${filtered.length} of ${blogs.length} blogs.`);
  }, [blogs, activeCategory]);

  // Load more blogs function - ALWAYS loads next sequential batch
  const loadMoreBlogs = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      // Always load the next sequential batch, regardless of category
      const { loadNextBatch } = await import('./services/searchService');
      const nextBatchData = await loadNextBatch(loadedBatches);

      if (nextBatchData.blogs.length > 0) {
        // Deduplicate blogs by ID
        const existingIds = new Set(blogs.map(b => b.id));
        const uniqueNewBlogs = nextBatchData.blogs.filter(b => !existingIds.has(b.id));

        if (uniqueNewBlogs.length > 0) {
          setBlogs(prevBlogs => [...prevBlogs, ...uniqueNewBlogs]);
        }
      }

      setHasMore(nextBatchData.hasMore);
      setLoadedBatches(nextBatchData.loadedBatches);

    } catch (err) {
      console.error('Error loading more blogs:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [blogs, loadedBatches, hasMore, loadingMore]);

  // Initial Load - ALWAYS load blogs regardless of current page
  // This ensures blogs are available when user navigates to Home from a blog detail page
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        console.log('📦 Loading initial batches...');
        const { loadInitialBlogs } = await import('./services/searchService');
        // Load first 3 batches
        const initialData = await loadInitialBlogs(3);

        setBlogs(initialData.blogs);
        // filteredBlogs will be updated by the other useEffect
        setHasMore(initialData.hasMore);
        setLoadedBatches(initialData.loadedBatches);
        setLoading(false);
      } catch (err) {
        console.error('Error loading initial blogs:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Handle category changes - reload data with new filter
  useEffect(() => {
    const loadCategoryData = async () => {
      // Check if we're on home page (not blog detail)
      const currentPath = window.location.pathname;
      const isHomePage = currentPath === '/' || currentPath.startsWith('/?');

      if (!isHomePage) return;

      // 1. First apply immediate filter on existing data
      const currentFiltered = filterBlogsByCategory(blogs, activeCategory);
      setFilteredBlogs(currentFiltered);

      // 2. If we don't have enough blogs (10) and have more batches, fetch more
      if (activeCategory !== 'all' && currentFiltered.length < 10 && hasMore && !loadingMore) {
        console.log(`📉 Only ${currentFiltered.length} blogs for ${activeCategory}. Fetching more...`);
        setLoadingMore(true); // User sees loading spinner at bottom

        try {
          const { loadNextBatch } = await import('./services/searchService');

          let currentBatches = loadedBatches;
          let newBlogsAccumulator = [];
          let foundCount = currentFiltered.length;
          let stillHasMore = true;
          let batchLimit = 0; // Safety break to prevent infinite loops (e.g. max 20 batches at once)

          // Loop until we have 10 blogs or run out of batches
          while (foundCount < 10 && stillHasMore && batchLimit < 20) {
            console.log(`🔄 Auto-fetching batch ${currentBatches + 1}...`);
            const nextBatchData = await loadNextBatch(currentBatches);

            if (nextBatchData.blogs.length > 0) {
              // Add to accumulator
              const existingIds = new Set([...blogs, ...newBlogsAccumulator].map(b => b.id));
              const uniqueNewBlogs = nextBatchData.blogs.filter(b => !existingIds.has(b.id));
              newBlogsAccumulator = [...newBlogsAccumulator, ...uniqueNewBlogs];

              // Check how many match our category
              const matchingInBatch = filterBlogsByCategory(uniqueNewBlogs, activeCategory);
              foundCount += matchingInBatch.length;
            }

            stillHasMore = nextBatchData.hasMore;
            currentBatches = nextBatchData.loadedBatches;
            batchLimit++;
          }

          // Update state with all accumulated blogs
          if (newBlogsAccumulator.length > 0) {
            setBlogs(prev => [...prev, ...newBlogsAccumulator]);
          }

          setLoadedBatches(currentBatches);
          setHasMore(stillHasMore);
          console.log(`✅ Auto-fetch done. Found total ${foundCount} blogs for ${activeCategory}.`);

        } catch (err) {
          console.error('Error in auto-fetch loop:', err);
        } finally {
          setLoadingMore(false);
        }
      }
    };

    loadCategoryData();
  }, [activeCategory]);

  // SEPARATE EFFECT: Keep filteredBlogs in sync with blogs state
  useEffect(() => {
    const filtered = filterBlogsByCategory(blogs, activeCategory);
    setFilteredBlogs(filtered);
  }, [blogs, activeCategory]);

  const handleCategoryChange = useCallback((category) => {
    setActiveCategory(category);
    if (category === 'all') {
      navigate('/', { replace: true });
    } else {
      navigate(`/category/${category}`, { replace: true });
    }
  }, [navigate]);

  return (
    <Routes>
      <Route
        path="/"
        element={
          <HomePage
            blogs={blogs}
            filteredBlogs={filteredBlogs}
            loading={loading}
            error={error}
            activeCategory={activeCategory}
            handleCategoryChange={handleCategoryChange}
            categories={categories}
            loadMoreBlogs={loadMoreBlogs}
            hasMore={hasMore}
            loadingMore={loadingMore}
          />
        }
      />
      <Route
        path="/category/:categoryId"
        element={
          <HomePage
            blogs={blogs}
            filteredBlogs={filteredBlogs}
            loading={loading}
            error={error}
            activeCategory={activeCategory}
            handleCategoryChange={handleCategoryChange}
            categories={categories}
            loadMoreBlogs={loadMoreBlogs}
            hasMore={hasMore}
            loadingMore={loadingMore}
          />
        }
      />
      <Route
        path="/blog/:id"
        element={
          <Suspense fallback={<div className="loading" style={{ minHeight: '60vh' }}>Loading article...</div>}>
            <BlogDetail
              blogs={blogs}
              categories={categories}
              onCategoryChange={handleCategoryChange}
              activeCategory={activeCategory}
            />
          </Suspense>
        }
      />
      <Route
        path="/about"
        element={
          <Suspense fallback={<div className="loading">Loading...</div>}>
            <About />
          </Suspense>
        }
      />
      <Route
        path="/contact"
        element={
          <Suspense fallback={<div className="loading">Loading...</div>}>
            <Contact />
          </Suspense>
        }
      />
      <Route
        path="*"
        element={
          <Suspense fallback={<div className="loading">Loading...</div>}>
            <NotFound />
          </Suspense>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AppWrapper />
      </Router>
    </HelmetProvider>
  );
}

export default App;
